import React, { useRef, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonText,
  IonIcon,
  useIonViewWillLeave,
} from '@ionic/react';
import { checkmarkCircleOutline } from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useUserContext } from '@/modules/user/useUserContext';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { useAppToast } from '@/hooks/useAppToast';
import PhoneVerificationModal from '@/modules/idv/PhoneVerificationModal';
import { ClaimsLogic } from '@/modules/idv';
import { PhoneLogic } from '@/modules/phone';
import { MePrFoService } from '@/services/meprfo';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';
import { pxToRem } from '@/modules/config/constants';

/**
 * PhoneMigrationNewPhoneStep (#441): the final step of a phone-number
 * migration. The caller has already PROVEN control of the existing account via
 * one of the proof methods (a code to the old number, the associated email, or
 * the security questions). Now they verify the NEW number, which tells the
 * backend to MOVE the whole profile onto it — keeping the current session and
 * everything keyed to the identity — rather than forking a new profile.
 *
 * The new number to verify is the one entered on the phone step (the wizard's
 * `phone`); the old number (`originalPhone`) rides along so initiation can text
 * it a change notice.
 */
const PhoneMigrationNewPhoneStep: React.FC = () => {
  const {
    userId: contextUserId,
    claims,
    setPhone: setContextPhone,
    setPendingPhone: setContextPendingPhone,
  } = useUserContext();
  const {
    phone,
    setOriginalPhone,
    originalPhone,
    urlProvidedOrigin,
    routerProvidedOrigin,
    navigateWithOrigin,
    navigateToStep,
    progress,
  } = useProfileWizardContext();
  const [presentToast] = useAppToast();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // Tracks a just-completed verification so the modal's trailing dismiss event
  // (which reports 'cancelled') doesn't revert the number we just verified.
  const verificationSucceededRef = useRef(false);

  const [showModal, setShowModal] = useState(false);

  // The new number's client-computed OPRF + tail. Passed to the verification
  // modal so initiation can tell the backend to move the profile onto the new
  // number once verified.
  const [migrateData, setMigrateData] = useState<
    { phoneOprf: string; phoneTail: string } | undefined
  >(undefined);

  const newPhoneObj = PhoneLogic.tryCreate(phone);
  const newPhoneValid = newPhoneObj !== null;

  // The gate the backend mirrors: a migration needs a live phone verification
  // or a live migration proof (claims.migrationProofExp). We should only reach
  // this step once the caller holds one; guard defensively regardless.
  const mayMigrate = ClaimsLogic.mayMigratePhone(claims);

  const determineEmailStep = (): string => '/profile/steps/email';

  const handleVerifyNew = async () => {
    if (!newPhoneObj) {
      presentToast({
        message: 'Enter a valid US phone number on the previous step first.',
        color: 'danger',
        duration: 3000,
      });
      return;
    }

    // Proof lapsed (e.g. the 5-minute window elapsed): send them back to prove
    // control of the old account again (by any method).
    if (!mayMigrate) {
      presentToast({
        message: 'Please verify it’s you again before switching numbers.',
        color: 'warning',
        duration: 4000,
      });
      navigateWithOrigin('/profile/steps/phone-migration-proof', 'back');
      return;
    }

    try {
      // One oblivious lookup gives us both the owner (to block an occupied
      // number) and the number's OPRF + tail (to tell initiation which number
      // to relink onto) — no second hash of the same number.
      const {
        userId: ownerOfNewNumber,
        phoneOprf,
        phoneTail,
      } = await MePrFoService.checkPhoneExists(newPhoneObj.value);

      if (ownerOfNewNumber && ownerOfNewNumber !== contextUserId) {
        // The target number is already a different profile; relinking onto an
        // occupied number is impossible.
        presentToast({
          message: 'That number already belongs to another Snowball profile.',
          color: 'danger',
          duration: 3000,
        });
        return;
      }

      setMigrateData({ phoneOprf, phoneTail });

      // The number stays pending until verification succeeds, so an abandoned
      // change never overwrites the stored number.
      setContextPendingPhone(newPhoneObj);
      setShowModal(true);
    } catch (error) {
      presentToast({
        message: error instanceof Error ? error.message : 'An error occurred',
        color: 'danger',
        duration: 3000,
      });
    }
  };

  const handleModalClose = (reason?: string) => {
    setShowModal(false);

    if (reason === 'success') {
      verificationSucceededRef.current = true;
      return;
    }
    if (verificationSucceededRef.current) {
      verificationSucceededRef.current = false;
      return;
    }

    // Cancelled or timed out before verifying: snap the app-wide context phone
    // back to the previously stored number so an unverified edit never sticks.
    if (reason === 'cancelled' || reason === 'expired') {
      const restored = originalPhone.trim()
        ? PhoneLogic.tryCreate(originalPhone)
        : null;
      setContextPhone(restored ?? undefined);
    }

    if (reason === 'expired') {
      presentToast({
        message: 'Verification code expired. Please try verifying again.',
        color: 'warning',
        duration: 5000,
      });
    }
  };

  const handleModalSuccess = () => {
    verificationSucceededRef.current = true;

    // The migration is done — commit the new number to durable storage and move
    // the wizard's change-detection snapshot onto it, so editing back to the old
    // number is correctly treated as a fresh migration rather than a silent fork.
    const verified = PhoneLogic.tryCreate(phone);
    if (verified) {
      setContextPhone(verified);
    }
    setOriginalPhone(phone);
    setShowModal(false);
    navigateWithOrigin(determineEmailStep(), 'forward');
  };

  const getContinuePath = (): string => {
    const emailStep = determineEmailStep();
    const origin = routerProvidedOrigin || urlProvidedOrigin || '/';
    return `${emailStep}?origin=${encodeURIComponent(origin)}`;
  };

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('phone-migration-new-phone')}
        progress={progress}
        onBack={() =>
          navigateToStep(
            ProfileWizardLogic.previousStep('phone-migration-new-phone'),
            'back'
          )
        }
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          <IonText>
            <p
              style={{
                fontSize: pxToRem(16),
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <IonIcon
                icon={checkmarkCircleOutline}
                style={{ color: 'var(--ion-color-success)' }}
              />
              It&rsquo;s you — verified.
            </p>
            <p style={{ fontSize: pxToRem(16), marginBottom: '10px' }}>
              Now verify your new number
              {newPhoneValid ? `, ${phone},` : ''} to finish relinking. All info
              from your existing profile remains in place.
            </p>
            {!newPhoneValid && (
              <p
                style={{
                  fontSize: pxToRem(13),
                  color: 'var(--ion-color-medium)',
                  marginBottom: '10px',
                }}
              >
                Head back to enter the new number you want to switch to.
              </p>
            )}
          </IonText>
        </div>
      </IonContent>

      <FooterDock background="transparent">
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleVerifyNew}
            disabled={!newPhoneValid}
            color="primary"
            data-testid="profile-wizard-next"
            style={{ margin: 0 }}
          >
            Verify new number &amp; relink
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>

      <PhoneVerificationModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        continuePath={getContinuePath()}
        previousPhone={originalPhone.trim() !== '' ? originalPhone : undefined}
        migrate={migrateData}
      />
    </IonPage>
  );
};

export default PhoneMigrationNewPhoneStep;
